/**
 * ContactCardFull - Comprehensive contact card with all DB fields
 * 
 * Displays ALL database fields from contact_intelligence_unified table
 * with inline editing capabilities and expandable sections
 */

import * as React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Mail,
  Calendar,
  Building,
  TrendingUp,
  TrendingDown,
  Minus,
  Edit,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  User,
  Target,
  Heart,
  MessageSquare,
  Clock,
  BarChart3,
  Settings,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { blink } from '@/lib/blink';
import { CIScorePill } from './CIScorePill';
import { PAMSSACPill } from './PAMSSACPill';

interface ContactCardFullProps {
  // Core fields
  id: string;
  personId: string;
  userId: string;
  contactEmail: string;
  name?: string;
  company?: string;
  
  // Interaction data
  firstInteractionAt?: string;
  lastInteractionAt?: string;
  totalInteractions: number;
  
  // Scores
  overallScore: number;
  isTop50?: boolean;
  rankPosition?: number;
  scoreTrend?: string;
  lastScoreUpdate?: string;
  
  // PAMSSAC scores
  proximity?: number;
  affinity?: number;
  mutuality?: number;
  status?: number;
  similarity?: number;
  availability?: number;
  complementarity?: number;
  
  // Relationship metrics
  relationshipHealth?: number;
  engagementLevel?: number;
  trustLevel?: number;
  strategicValue?: number;
  
  // Communication data
  avgResponseTimeHours?: number;
  totalSent?: number;
  totalReceived?: number;
  communicationFrequency?: string;
  preferredChannel?: string;
  communicationStyle?: string;
  
  // Intelligence data
  relationshipSummary?: string;
  goalsTop5?: string;
  needsTop5?: string;
  wantsTop5?: string;
  canHelpWithTop3?: string;
  emotionalSignature?: string;
  preferenceCues?: string;
  reciprocityLedger?: string;
  
  // AI insights
  aiRelationshipScore?: number;
  aiSummary?: string;
  behavioralInsights?: string;
  predictiveInsights?: string;
  actionRecommendations?: string;
  whyImportant?: string;
  evidenceThreads?: string;
  timelineInsights?: string;
  openCommitments?: string;
  
  // Suggested actions
  suggestedActionType?: string;
  suggestedActionReason?: string;
  suggestedActionTemplate?: string;
  suggestedActionPriority?: number;
  suggestedActionDueBy?: string;
  
  // Metadata
  confidenceScore?: number;
  dataCompleteness?: number;
  lastAnalyzedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  
  // Callbacks
  onUpdate?: (data: any) => void;
  onClick?: () => void;
  isSelected?: boolean;
}

export function ContactCardFull(props: ContactCardFullProps) {
  const [editMode, setEditMode] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  
  // Editable fields state
  const [editData, setEditData] = React.useState({
    name: props.name || '',
    company: props.company || '',
    relationshipSummary: props.relationshipSummary || '',
    goalsTop5: props.goalsTop5 || '',
    needsTop5: props.needsTop5 || '',
    wantsTop5: props.wantsTop5 || '',
    canHelpWithTop3: props.canHelpWithTop3 || '',
    communicationStyle: props.communicationStyle || '',
    preferredChannel: props.preferredChannel || '',
    whyImportant: props.whyImportant || '',
  });
  
  const getInitials = () => {
    if (props.name) {
      return props.name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase().slice(0, 2) || '??';
    }
    return props.contactEmail?.[0]?.toUpperCase() || '??';
  };
  
  const getTrendIcon = () => {
    if (!props.scoreTrend) return <Minus className="w-3 h-3 text-muted-foreground" />;
    if (props.scoreTrend === 'up') return <TrendingUp className="w-3 h-3 text-green-500" />;
    if (props.scoreTrend === 'down') return <TrendingDown className="w-3 h-3 text-red-500" />;
    return <Minus className="w-3 h-3 text-muted-foreground" />;
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const user = await blink.auth.me();
      if (!user) {
        toast.error('Please sign in to save changes');
        return;
      }
      
      // Update contact in database
      await blink.db.contactIntelligenceUnified.update(props.id, {
        name: editData.name,
        company: editData.company,
        relationshipSummary: editData.relationshipSummary,
        goalsTop5: editData.goalsTop5,
        needsTop5: editData.needsTop5,
        wantsTop5: editData.wantsTop5,
        canHelpWithTop3: editData.canHelpWithTop3,
        communicationStyle: editData.communicationStyle,
        preferredChannel: editData.preferredChannel,
        whyImportant: editData.whyImportant,
        updatedAt: new Date().toISOString(),
      } as any);
      
      toast.success('Contact updated successfully');
      setEditMode(false);
      
      // Call parent update callback
      if (props.onUpdate) {
        props.onUpdate(editData);
      }
    } catch (error) {
      console.error('Failed to save contact:', error);
      toast.error('Failed to save contact');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    // Reset to original values
    setEditData({
      name: props.name || '',
      company: props.company || '',
      relationshipSummary: props.relationshipSummary || '',
      goalsTop5: props.goalsTop5 || '',
      needsTop5: props.needsTop5 || '',
      wantsTop5: props.wantsTop5 || '',
      canHelpWithTop3: props.canHelpWithTop3 || '',
      communicationStyle: props.communicationStyle || '',
      preferredChannel: props.preferredChannel || '',
      whyImportant: props.whyImportant || '',
    });
    setEditMode(false);
  };
  
  return (
    <Card 
      className={`glass-card border-border/50 transition-all hover:border-primary/50 ${props.isSelected ? 'border-primary shadow-lg' : ''}`}
      onClick={(e) => {
        // Only trigger onClick if not clicking on input/button
        if (e.target instanceof HTMLElement && !e.target.closest('input, textarea, button')) {
          props.onClick?.();
        }
      }}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          {/* Avatar and basic info */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Avatar className="h-12 w-12 ring-2 ring-primary/20">
              <AvatarFallback className="text-lg font-semibold bg-gradient-to-br from-primary/20 to-accent/20">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0">
              {editMode ? (
                <div className="space-y-2">
                  <Input
                    value={editData.name}
                    onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Name"
                    className="h-8"
                  />
                  <Input
                    value={editData.company}
                    onChange={(e) => setEditData(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Company"
                    className="h-7 text-sm"
                  />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg">{props.name || props.contactEmail}</CardTitle>
                    {props.isTop50 && (
                      <Badge className="bg-accent/20 text-accent">Top 50</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                    <Building className="w-3 h-3" />
                    {props.company || 'No company'}
                  </p>
                </>
              )}
            </div>
          </div>
          
          {/* Scores and actions */}
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <CIScorePill score={props.overallScore} trend={props.scoreTrend as any} />
              {getTrendIcon()}
            </div>
            
            {!editMode ? (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditMode(true);
                }}
                className="h-7 gap-1"
              >
                <Edit className="w-3 h-3" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                  className="h-7 gap-1"
                >
                  <X className="w-3 h-3" />
                </Button>
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSave();
                  }}
                  className="h-7 gap-1 ci-gradient-gold text-[#0a0a0b]"
                  disabled={saving}
                >
                  <Save className="w-3 h-3" />
                  Save
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {/* Quick stats row */}
        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Mail className="w-3 h-3" />
            {props.contactEmail}
          </div>
          <div className="flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />
            {props.totalInteractions} interactions
          </div>
          {props.lastInteractionAt && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {format(new Date(props.lastInteractionAt), 'MMM d, yyyy')}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* PAMSSAC Scores */}
        {(props.proximity !== undefined || props.affinity !== undefined) && (
          <div>
            <PAMSSACPill
              P={props.proximity || 0}
              A1={props.affinity || 0}
              M={props.mutuality || 0}
              S1={props.status || 0}
              S2={props.similarity || 0}
              A2={props.availability || 0}
              C={props.complementarity || 0}
              total={props.overallScore}
            />
          </div>
        )}
        
        {/* Expandable sections */}
        <Collapsible open={expanded} onOpenChange={setExpanded}>
          <CollapsibleTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-between glass-card border-border/50"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                {expanded ? 'Hide' : 'Show'} All Fields
              </span>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </CollapsibleTrigger>
          
          <CollapsibleContent className="space-y-4 mt-4">
            {/* Relationship Summary */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Relationship Summary
              </label>
              {editMode ? (
                <Textarea
                  value={editData.relationshipSummary}
                  onChange={(e) => setEditData(prev => ({ ...prev, relationshipSummary: e.target.value }))}
                  placeholder="Describe the relationship..."
                  rows={2}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {props.relationshipSummary || 'No summary available'}
                </p>
              )}
            </div>
            
            <Separator />
            
            {/* Goals, Needs, Wants */}
            <div className="grid gap-3">
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Their Goals (Top 5)
                </label>
                {editMode ? (
                  <Textarea
                    value={editData.goalsTop5}
                    onChange={(e) => setEditData(prev => ({ ...prev, goalsTop5: e.target.value }))}
                    placeholder="Goals..."
                    rows={2}
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    {props.goalsTop5 || 'Not specified'}
                  </p>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium">Their Needs (Top 5)</label>
                {editMode ? (
                  <Textarea
                    value={editData.needsTop5}
                    onChange={(e) => setEditData(prev => ({ ...prev, needsTop5: e.target.value }))}
                    placeholder="Needs..."
                    rows={2}
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    {props.needsTop5 || 'Not specified'}
                  </p>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium">Their Wants (Top 5)</label>
                {editMode ? (
                  <Textarea
                    value={editData.wantsTop5}
                    onChange={(e) => setEditData(prev => ({ ...prev, wantsTop5: e.target.value }))}
                    placeholder="Wants..."
                    rows={2}
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    {props.wantsTop5 || 'Not specified'}
                  </p>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium">How You Can Help (Top 3)</label>
                {editMode ? (
                  <Textarea
                    value={editData.canHelpWithTop3}
                    onChange={(e) => setEditData(prev => ({ ...prev, canHelpWithTop3: e.target.value }))}
                    placeholder="How you can help..."
                    rows={2}
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    {props.canHelpWithTop3 || 'Not specified'}
                  </p>
                )}
              </div>
            </div>
            
            <Separator />
            
            {/* Communication preferences */}
            <div className="grid gap-3">
              <div>
                <label className="text-sm font-medium">Communication Style</label>
                {editMode ? (
                  <Input
                    value={editData.communicationStyle}
                    onChange={(e) => setEditData(prev => ({ ...prev, communicationStyle: e.target.value }))}
                    placeholder="e.g., Direct, Formal, Casual"
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    {props.communicationStyle || 'Not specified'}
                  </p>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium">Preferred Channel</label>
                {editMode ? (
                  <Input
                    value={editData.preferredChannel}
                    onChange={(e) => setEditData(prev => ({ ...prev, preferredChannel: e.target.value }))}
                    placeholder="e.g., Email, Phone, Slack"
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    {props.preferredChannel || 'Not specified'}
                  </p>
                )}
              </div>
            </div>
            
            <Separator />
            
            {/* AI Insights */}
            {props.aiSummary && (
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  AI Summary
                </label>
                <p className="text-sm text-muted-foreground mt-1">{props.aiSummary}</p>
              </div>
            )}
            
            {/* Why Important */}
            <div>
              <label className="text-sm font-medium">Why Important</label>
              {editMode ? (
                <Textarea
                  value={editData.whyImportant}
                  onChange={(e) => setEditData(prev => ({ ...prev, whyImportant: e.target.value }))}
                  placeholder="Why is this relationship important?"
                  rows={2}
                  className="mt-1"
                />
              ) : (
                <p className="text-sm text-muted-foreground mt-1">
                  {props.whyImportant || 'Not specified'}
                </p>
              )}
            </div>
            
            {/* Metadata */}
            <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
              {props.confidenceScore !== undefined && (
                <div>
                  <span className="font-medium">Confidence:</span> {props.confidenceScore}%
                </div>
              )}
              {props.dataCompleteness !== undefined && (
                <div>
                  <span className="font-medium">Data Completeness:</span> {props.dataCompleteness}%
                </div>
              )}
              {props.lastAnalyzedAt && (
                <div className="col-span-2">
                  <span className="font-medium">Last Analyzed:</span>{' '}
                  {format(new Date(props.lastAnalyzedAt), 'MMM d, yyyy h:mm a')}
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
